/**
 * Analítica diária de faltas e jornadas curtas normalizada por colaboradores ativos.
 *
 * Produz, para cada data do período analisado, a proporção de colaboradores
 * afetados por faltas (ausência total em dia de escala) e por jornadas curtas
 * (atraso ou saída antecipada que supere a tolerância).
 *
 * Privacidade: nenhum dado pessoal (CPF, nome) circula aqui — apenas
 * AnonymousId e datas agregadas.
 */

import type { DailyJourney } from '../domain/canonical.types';

// =========================================================================
// Tipos públicos
// =========================================================================

/** Um ponto de dado agregado por dia para o gráfico de faltas e jornadas curtas. */
export interface DailyAbsencePoint {
  /** Data no formato AAAA-MM-DD. */
  date: string;
  /**
   * Colaboradores ativos no dia = que tinham jornada esperada (scheduledDay)
   * OU que bateram ponto (workedMinutes > 0). Denominador das proporções.
   */
  activeEmployees: number;
  /** Colaboradores em falta total (scheduledDay=true, workedMinutes=0). */
  absenceCount: number;
  /**
   * Colaboradores com jornada curta: tinham escala, bateram ponto,
   * mas trabalharam menos do que o esperado menos a tolerância.
   */
  shortJourneyCount: number;
  /** % de faltas: absenceCount / activeEmployees * 100. NaN quando activeEmployees=0. */
  absencePct: number;
  /** % de jornadas curtas: shortJourneyCount / activeEmployees * 100. */
  shortJourneyPct: number;
}

// =========================================================================
// Implementação
// =========================================================================

/**
 * Agrega jornadas diárias por data, calculando proporções de faltas e
 * jornadas curtas em relação aos colaboradores ativos em cada dia.
 *
 * @param journeys        Array de DailyJourney produzido por buildDailyJourneys.
 * @param toleranceMinutes  Minutos de tolerância (mesma premissa do usuário).
 */
export function buildDailyAbsenceAnalytics(
  journeys: DailyJourney[],
  toleranceMinutes: number,
): DailyAbsencePoint[] {
  if (journeys.length === 0) return [];

  // Agrupa jornadas por data
  const byDate = new Map<string, DailyJourney[]>();
  for (const j of journeys) {
    let arr = byDate.get(j.operationalDate);
    if (arr === undefined) {
      arr = [];
      byDate.set(j.operationalDate, arr);
    }
    arr.push(j);
  }

  // Ordena datas cronologicamente
  const sortedDates = [...byDate.keys()].sort();

  const points: DailyAbsencePoint[] = [];

  for (const date of sortedDates) {
    const dayJourneys = byDate.get(date)!;

    let activeEmployees = 0;
    let absenceCount = 0;
    let shortJourneyCount = 0;

    for (const j of dayJourneys) {
      const hasSchedule = j.scheduledDay;
      const hasWorked = j.workedMinutes > 0;

      // Ativo = tinha escala OU bateu ponto
      if (!hasSchedule && !hasWorked) continue;
      activeEmployees++;

      if (hasSchedule && !hasWorked) {
        // Ausência total: tinha escala mas não bateu ponto
        absenceCount++;
      } else if (hasSchedule && hasWorked) {
        // Jornada curta: bateu ponto mas ficou abaixo da tolerância
        const deficit = j.expectedMinutes - j.workedMinutes;
        if (deficit > toleranceMinutes) {
          shortJourneyCount++;
        }
      }
    }

    const absencePct =
      activeEmployees > 0 ? (absenceCount / activeEmployees) * 100 : 0;
    const shortJourneyPct =
      activeEmployees > 0 ? (shortJourneyCount / activeEmployees) * 100 : 0;

    points.push({
      date,
      activeEmployees,
      absenceCount,
      shortJourneyCount,
      absencePct,
      shortJourneyPct,
    });
  }

  return points;
}

// =========================================================================
// KPIs derivados
// =========================================================================

export interface AbsenceChartKpis {
  /** Dia com maior % de faltas. */
  peakAbsenceDate: string | null;
  /** % de faltas no pico. */
  peakAbsencePct: number;
  /** Quantidade absoluta de faltas no pico. */
  peakAbsenceCount: number;
  /** Média de % de faltas ao longo do período. */
  avgAbsencePct: number;
  /** Total de ocorrências de faltas no período. */
  totalAbsences: number;
  /** Total de ocorrências de jornadas curtas no período. */
  totalShortJourneys: number;
}

export function calcAbsenceChartKpis(
  points: DailyAbsencePoint[],
): AbsenceChartKpis {
  if (points.length === 0) {
    return {
      peakAbsenceDate: null,
      peakAbsencePct: 0,
      peakAbsenceCount: 0,
      avgAbsencePct: 0,
      totalAbsences: 0,
      totalShortJourneys: 0,
    };
  }

  let peakAbsencePct = 0;
  let peakAbsenceDate: string | null = null;
  let peakAbsenceCount = 0;
  let sumAbsencePct = 0;
  let totalAbsences = 0;
  let totalShortJourneys = 0;

  for (const p of points) {
    sumAbsencePct += p.absencePct;
    totalAbsences += p.absenceCount;
    totalShortJourneys += p.shortJourneyCount;

    if (p.absencePct > peakAbsencePct) {
      peakAbsencePct = p.absencePct;
      peakAbsenceDate = p.date;
      peakAbsenceCount = p.absenceCount;
    }
  }

  const avgAbsencePct = sumAbsencePct / points.length;

  return {
    peakAbsenceDate,
    peakAbsencePct,
    peakAbsenceCount,
    avgAbsencePct,
    totalAbsences,
    totalShortJourneys,
  };
}
